import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Route, Router, UrlSegment } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { PermissionsService } from 'src/app/core/menu/permissions.service';
import { MENU_SVG_ICONS } from 'src/app/core/menu/menu-svg-icons';
import { UserPermissions } from 'src/app/models/user-permissions.interface';
import { boSwalIcon } from 'src/app/shared/utils/bo-swal';

/** Empresa ficticia del dashboard de ejemplo («Ejemplo S.L.»). */
export const DEMO_CORPORATION_ID = 'ejemplo';
export const DEMO_COMPANY_NAME = 'Ejemplo S.L.';

/** Páginas del ejemplo, bajo `/demo/corporation/ejemplo/`. */
export const DEMO_PAGES = ['routes-map', 'usage', 'users', 'travel', 'funnel', 'pmst'];

/** Paso 1 del registro: a donde lleva «Crear mi espacio». */
export const DEMO_SIGNUP_ROUTE = ['/auth/registration'];

/**
 * Sesión ficticia del dashboard de ejemplo: el administrador de «Ejemplo S.L.»,
 * con las páginas que enseña el ejemplo. Solo vive mientras se está en `/demo`.
 */
const DEMO_PERMISSIONS: UserPermissions = {
  id: 'demo',
  name: DEMO_COMPANY_NAME,
  email: '',
  roles: [
    {
      role_name: 'corporation_admin',
      permission_name: 'corporation_admin',
      scope_type: 'corporation',
      scope_id: DEMO_CORPORATION_ID,
    } as UserPermissions['roles'][number],
  ],
  pages: [],
  actions: [],
  partner: null,
  tutored: [],
  trial: null,
};

/**
 * Dashboard de ejemplo sin registro (#20261023-090014): mientras está activo,
 * `DemoInterceptor` responde las lecturas con datos fijos y corta las
 * escrituras con «Para guardar, crea tu espacio». Se entra por la ruta
 * pública `/demo`; al salir, vuelve la sesión que hubiera.
 */
@Injectable({ providedIn: 'root' })
export class DemoModeService {
  active = false;
  private previous: UserPermissions | null = null;
  private blocking = false;

  constructor(
    private permissionsService: PermissionsService,
    private router: Router,
    private translate: TranslateService,
  ) {
    // Red de seguridad: si una navegación acaba (o se corta) fuera de `/demo`
    // con el ejemplo aún activo, se sale; si no, el interceptor dejaría el
    // resto de la app sin back.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError))
      .subscribe(() => {
        if (this.active && !isDemoUrl(this.router.url)) this.leave();
      });
  }

  enter(): void {
    if (this.active) return;
    this.active = true;
    this.previous = this.permissionsService.currentPermissions;
    this.permissionsService.setCurrentCorporationId(DEMO_CORPORATION_ID);
    this.permissionsService.setPermissions(DEMO_PERMISSIONS);
  }

  leave(): void {
    if (!this.active) return;
    this.active = false;
    this.permissionsService.setCurrentCorporationId(null);
    if (this.previous) {
      this.permissionsService.setPermissions(this.previous);
    } else {
      this.permissionsService.clearPermissions();
    }
    this.previous = null;
  }

  /** CR8: lo que guardaría algo se corta con este aviso, una sola vez a la vez. */
  blockWrite(): void {
    if (this.blocking) return;
    this.blocking = true;
    const t = (key: string) => this.translate.instant(key);
    Swal.fire({
      ...boSwalIcon(MENU_SVG_ICONS['sparkles'], 'brand'),
      title: t('DEMO.SAVE_BLOCKED.TITLE'),
      text: t('DEMO.SAVE_BLOCKED.TEXT'),
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: t('DEMO.CREATE_SPACE'),
      cancelButtonText: t('DEMO.KEEP_EXPLORING'),
    }).then((result) => {
      this.blocking = false;
      if (result.isConfirmed) this.router.navigate(DEMO_SIGNUP_ROUTE);
    });
  }
}

function isDemoUrl(url: string): boolean {
  return /^\/demo(\/|\?|#|$)/.test(url);
}

/**
 * Las páginas del ejemplo navegan con rutas absolutas de su empresa
 * (`/corporation/ejemplo/...`), que sacarían de `/demo` al login. Con el ejemplo
 * abierto, esta ruta (en `app-routing`, antes que el resto) las recoge: las
 * páginas del ejemplo se quedan dentro de `/demo` y lo demás (crear, detalles,
 * secciones que no están en el ejemplo) se corta con el aviso de CR8.
 * Fuera del ejemplo no casa y la navegación sigue como siempre.
 */
export const DEMO_ESCAPE_ROUTE: Route = {
  matcher: (segments: UrlSegment[]) =>
    segments[0]?.path === 'corporation' && segments[1]?.path === DEMO_CORPORATION_ID ? { consumed: segments } : null,
  canMatch: [() => inject(DemoModeService).active],
  canActivate: [
    (_route: unknown, state: { url: string }) => {
      const router = inject(Router);
      const tree = router.parseUrl(state.url);
      const segments = tree.root.children['primary']?.segments.map((s) => s.path) ?? [];
      if (segments.length === 3 && DEMO_PAGES.includes(segments[2])) {
        return router.createUrlTree(['/demo', ...segments], { queryParams: tree.queryParams });
      }
      inject(DemoModeService).blockWrite();
      return false;
    },
  ],
  children: [],
};
